import type { MerrySearchableMultiSelectGroup } from "@/shared/ui/MerrySearchableMultiSelect";

export type OnboardingSelectGroup = MerrySearchableMultiSelectGroup;

export const onboardingMarketIds = [
  "southeast-asia",
  "europe",
  "uk",
  "north-america",
  "us",
  "latin-america",
] as const;

export type OnboardingMarketId = (typeof onboardingMarketIds)[number];

export const onboardingBStartMethodOptions = [
  { id: "dropshipping", label: "一件代发，有订单再采购", letter: "A" },
  { id: "small-stock", label: "先拿少量现货测试", letter: "B" },
  {
    id: "inventory-7-14-days",
    label: "可以准备 7—14 天库存",
    letter: "C",
  },
  { id: "overseas-warehouse", label: "已经有海外仓", letter: "D" },
  { id: "no-fixed-method", label: "还没有固定方式", letter: "E" },
] as const;

export type OnboardingBStartMethodId =
  (typeof onboardingBStartMethodOptions)[number]["id"];

export const onboardingBAlertTimingOptions = [
  {
    id: "after-no-drop",
    label: "连续多天没有回落后再推荐给我",
    letter: "A",
  },
  {
    id: "when-clear",
    label: "趋势已经比较明确时就告诉我",
    letter: "B",
  },
  {
    id: "show-all-with-risk",
    label: "早期和稳定机会都展示，但把风险写清楚",
    letter: "C",
  },
  { id: "no-preference", label: "暂时没有偏好", letter: "D" },
] as const;

export type OnboardingBAlertTimingId =
  (typeof onboardingBAlertTimingOptions)[number]["id"];

export const onboardingBGrowthWindowOptions = [
  { id: "thirty-days", label: "至少还有 30 天", letter: "A" },
  { id: "fourteen-days", label: "至少还有 14 天", letter: "B" },
  { id: "seven-days", label: "至少还有 7 天", letter: "C" },
  {
    id: "self-judge",
    label: "只要还能增长，我会自己判断",
    letter: "D",
  },
  { id: "unsure", label: "暂时不确定", letter: "E" },
] as const;

export type OnboardingBGrowthWindowId =
  (typeof onboardingBGrowthWindowOptions)[number]["id"];

export const onboardingCProductStageOptions = [
  {
    id: "exploring-direction",
    label: "还在寻找值得进入的方向",
    letter: "A",
  },
  {
    id: "validating-idea",
    label: "已有产品想法，正在验证需求",
    letter: "B",
  },
  {
    id: "preparing-sample",
    label: "已经找到工厂，准备打样",
    letter: "C",
  },
  {
    id: "testing-market",
    label: "已经有样品，准备测试市场",
    letter: "D",
  },
  {
    id: "selling-branded-product",
    label: "已经有品牌和产品在销售",
    letter: "E",
  },
] as const;

export type OnboardingCProductStageId =
  (typeof onboardingCProductStageOptions)[number]["id"];

export const onboardingCPriceRangeOptions = [
  { id: "low-volume", label: "低价走量", letter: "A" },
  { id: "mass-market", label: "大众价格带", letter: "B" },
  { id: "mid-premium", label: "中高端价格带", letter: "C" },
  {
    id: "premium-professional",
    label: "高端或专业产品",
    letter: "D",
  },
  { id: "undecided", label: "还没有确定", letter: "E" },
] as const;

export type OnboardingCPriceRangeId =
  (typeof onboardingCPriceRangeOptions)[number]["id"];

export const onboardingCPrimaryGoalOptions = [
  {
    id: "find-category-price",
    label: "找到值得进入的品类和价格带",
    letter: "A",
  },
  {
    displayLabel: "找出现有商品被用户吐槽的\n问题",
    id: "find-product-complaints",
    label: "找出现有商品被用户吐槽的问题",
    letter: "B",
  },
  {
    id: "validate-demand",
    label: "判断自己的产品有没有需求",
    letter: "C",
  },
  {
    id: "find-selling-points",
    label: "找到适合宣传的产品卖点",
    letter: "D",
  },
  {
    id: "differentiate-competitors",
    label: "分析竞品并做出差异化",
    letter: "E",
  },
  { id: "undecided", label: "暂时还不确定", letter: "F" },
] as const;

export type OnboardingCPrimaryGoalId =
  (typeof onboardingCPrimaryGoalOptions)[number]["id"];

export const onboardingDEntryStateOptions = [
  {
    fontWeight: 700,
    id: "explore-opportunities",
    label: "想了解跨境电商有哪些机会",
    letter: "A",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "considering-store",
    label: "正在考虑开店",
    letter: "B",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "store-no-product",
    label: "已经开店，但还没确定卖什么",
    letter: "C",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "selling-seeking-growth",
    label: "已经在销售，想寻找新的增长方向",
    letter: "D",
    lineHeight: 18,
  },
] as const;

export type OnboardingDEntryStateId =
  (typeof onboardingDEntryStateOptions)[number]["id"];

export const onboardingDContentGoalOptions = [
  {
    fontWeight: 700,
    id: "recent-product-opportunities",
    label: "最近有哪些商品机会",
    letter: "A",
    lineHeight: 14,
  },
  {
    fontWeight: 500,
    id: "valuable-markets-categories",
    label: "哪些国家和品类更值得做",
    letter: "B",
    lineHeight: 18,
  },
  {
    fontWeight: 700,
    id: "find-suitable-product",
    label: "怎样找到适合自己的产品",
    letter: "C",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "start-store-business",
    label: "怎样开店和启动业务",
    letter: "D",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "improve-existing-store",
    label: "怎样把现有店铺做得更好",
    letter: "E",
    lineHeight: 14,
  },
  {
    fontWeight: 700,
    id: "browse-first",
    label: "先随便看看",
    letter: "F",
    lineHeight: 14,
  },
] as const;

export type OnboardingDContentGoalId =
  (typeof onboardingDContentGoalOptions)[number]["id"];

export const onboardingMarketGroups = [
  {
    id: "asia",
    label: "亚洲",
    options: [
      {
        id: "southeast-asia",
        keywords: ["东南亚", "亚洲"],
        label: "东南亚地区",
      },
    ],
  },
  {
    id: "europe",
    label: "欧洲",
    options: [
      { id: "europe", keywords: ["欧盟"], label: "欧洲地区" },
      { id: "uk", keywords: ["英国", "英格兰"], label: "英国" },
    ],
  },
  {
    id: "north-america",
    label: "北美洲",
    options: [
      {
        id: "north-america",
        keywords: ["北美"],
        label: "北美洲地区",
      },
      { id: "us", keywords: ["美国", "美利坚"], label: "美国" },
    ],
  },
  {
    id: "south-america",
    label: "南美洲",
    options: [
      {
        id: "latin-america",
        keywords: ["拉美", "南美"],
        label: "拉美地区",
      },
    ],
  },
] as const satisfies readonly OnboardingSelectGroup[];

export function getOnboardingOptionLabel(
  groups: readonly OnboardingSelectGroup[],
  optionId: string,
) {
  for (const group of groups) {
    const option = group.options.find((item) => item.id === optionId);

    if (option) {
      return option.label;
    }
  }

  return optionId;
}
